import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createWorkCenter, updateWorkCenter } from "../api/workCenters";
import { listWorkCenterGroups } from "../api/workCenterGroups";

function WorkCenterModal({ data, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [serialCode, setSerialCode] = useState("");
  const [type, setType] = useState("");
  const [groupId, setGroupId] = useState("");
  const [headCount, setHeadCount] = useState("");
  const [allGroups, setAllGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selectedGroup = allGroups.find((group) => String(group.id) === String(groupId));
  const isForming = String(selectedGroup?.code || "").toUpperCase() === "FRM";

  useEffect(() => {
    listWorkCenterGroups({ includeInactive: false })
      .then((items) => setAllGroups(items || []))
      .catch(() => setAllGroups([]));
  }, []);

  useEffect(() => {
    if (data) {
      setCode(data.code || "");
      setName(data.name || "");
      setSerialCode(data.serial_code || "");
      setType(data.type || "");
      setGroupId(data.group_id ? String(data.group_id) : "");
      setHeadCount(data.head_count == null ? "" : String(data.head_count));
    } else {
      setCode("");
      setName("");
      setSerialCode("");
      setType("");
      setGroupId("");
      setHeadCount("");
    }
  }, [data]);

  async function save() {
    setError("");
    const c = String(code || "").trim();
    const n = String(name || "").trim();
    if (!c || !n) {
      setError("Kod ve ad zorunlu.");
      return;
    }
    const normalizedHeadCount = isForming ? Number(headCount) : null;
    if (isForming && ![2, 4].includes(normalizedHeadCount)) {
      setError("Formlama makinelerinde kafa sayısı 2 veya 4 olmalıdır.");
      return;
    }

    const payload = {
      code: c,
      name: n,
      serial_code: String(serialCode || "").trim() || null,
      type: String(type || "").trim() || null,
      group_id: groupId ? Number(groupId) : null,
      head_count: normalizedHeadCount,
    };

    const req = data?.id ? updateWorkCenter(data.id, payload) : createWorkCenter(payload);
    setSaving(true);
    try {
      await req;
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("WORK CENTER SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const details = dataObj?.error?.details;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      const detailsText =
        details && typeof details === "object"
          ? details.sqlMessage || details.key || details.db_code
          : typeof details === "string"
            ? details
            : null;
      setError(
        `Kaydedilemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}${
          detailsText ? `\nDetay: ${detailsText}` : ""
        }`
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {data ? "Makine Düzenle" : "Yeni Makine"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          İş merkezi/makine bilgilerini düzenleyin.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Kod" value={code} onChange={(e) => setCode(e.target.value)} required disabled={saving} />
          <TextField label="Ad" value={name} onChange={(e) => setName(e.target.value)} required disabled={saving} />
          <TextField
            label="Seri Kodu"
            value={serialCode}
            onChange={(e) => setSerialCode(e.target.value)}
            disabled={saving}
            helperText="Opsiyonel; aynı şirket içinde benzersiz olmalıdır"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Tip (Baskı/Kesim)"
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={saving}
            helperText="Opsiyonel"
          />
          <TextField
            select
            label="Makine Grubu"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            disabled={saving}
            helperText="Bu makinenin dahil olduğu grubu seçin"
          >
            <MenuItem value="">
              <em>Grup yok / seçme</em>
            </MenuItem>
            {allGroups.map((wg) => (
              <MenuItem key={wg.id} value={String(wg.id)}>
                {wg.code} — {wg.name}
              </MenuItem>
            ))}
          </TextField>
          {isForming ? (
            <TextField
              select
              label="Kafa Sayısı"
              value={headCount}
              onChange={(e) => setHeadCount(e.target.value)}
              disabled={saving}
              required
              helperText="Formlama makinelerinde 2 veya 4 kafa seçin"
            >
              <MenuItem value="">Kafa sayısı seçin</MenuItem>
              <MenuItem value="2">2 kafa (A, B)</MenuItem>
              <MenuItem value="4">4 kafa (A, B, C, D)</MenuItem>
            </TextField>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          İptal
        </Button>
        <Button onClick={save} variant="contained" disabled={saving}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkCenterModal;
